import React from 'react';

import { Router } from 'react-router-dom';

import { css, cx } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantIntegrationConnectionStatus } from '@recurrency/core-api-schema/dist/tenants/tenantUserDTO';
import { Grid } from 'antd';

import { hasChatWithDataAccess } from 'pages/chat/access';

import { Alert } from 'components/Alert';
import { ChatAssistant } from 'components/ChatAssistant/ChatAssistant';
import { ChatContextProvider } from 'components/ChatAssistant/ChatContext';
import { IntercomUpdater } from 'components/Intercom/IntercomUpdater';
import { CenteredAccountError } from 'components/Loaders';
import { UpgradeBanner } from 'components/recipes/Upgrade/UpgradeBanner';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { browserHistory } from 'utils/browserHistory';
import { env } from 'utils/env';

import { AppRoutes } from './frames/AppRoutes';
import { AppVersionPoller } from './frames/AppVersionPoller';
import { Auth0AppStateLoader } from './frames/Auth0AppStateLoader';
import { ErrorBoundary } from './frames/ErrorBoundary';
import { IntegrationPausedBanner } from './frames/IntegrationPausedBanner';
import { MaintenancePage } from './frames/MaintenancePage';
import { Navigation } from './frames/Navigation';
import { NoConnectionBanner } from './frames/NoConnectionBanner';

export function App() {
  const showMaintenancePage = false;

  return (
    <Router history={browserHistory}>
      <ErrorBoundary>
        {showMaintenancePage ? (
          <MaintenancePage />
        ) : (
          <Auth0AppStateLoader
            domain={env.AUTH0_DOMAIN}
            client_id={env.AUTH0_CLIENT_ID}
            audience={env.AUTH0_AUDIENCE}
            redirect_uri={window.location.origin}
            scope="openid profile email recurrency:user recurrency:admin recurrency:staff:user"
            cacheLocation="localstorage"
            // only setting this for staging and dev environments to guage user experience impact
            useRefreshTokens={env.APP_ENV !== 'production'}
          >
            <AppVersionPoller>
              <AppContainer>
                <AppRoutes />
              </AppContainer>
            </AppVersionPoller>
          </Auth0AppStateLoader>
        )}
      </ErrorBoundary>
    </Router>
  );
}

function AppContainer({ children }: { children: JSX.Element }) {
  const { activeUser, activeTenant } = useGlobalApp();

  const isDesktopView: boolean = Grid.useBreakpoint()?.md ?? false;

  const { data, isLoading } = useCoreApi(schemas.tenants.getTenantIntegrationConnectionsStatusDetails);
  const dataSyncConnectionApi = data?.connectionApi?.dataSync;
  const dataSyncAvailable =
    dataSyncConnectionApi &&
    data?.connectionStatus?.[dataSyncConnectionApi]?.status === TenantIntegrationConnectionStatus.Available;
  const stunnelAvailable = data?.stunnelStatus?.status === TenantIntegrationConnectionStatus.Available;

  // If stunnel is unavailable, we can't verify their erp connection, so we don't show the connection banner
  const isConnectionDown = !isLoading && stunnelAvailable && !dataSyncAvailable && activeTenant.isActive;
  const connectionBannerText = (
    <div>
      Recurrency cannot connect to your ERP. Updates may be delayed between Recurrency and your ERP. Instructions for
      troubleshooting and resolving these issues{' '}
      <a
        target="none"
        href="https://help.recurrency.com/hc/en-us/articles/36521138226971-Resolving-Issues-with-Integration-Connections-the-Recurrency-Integration-Agent"
      >
        can be found on our help center
      </a>
      . Please reach out to your Recurrency contact or solutions@recurrency.com for more details.
    </div>
  );

  // don't render app if user doesn't load or has no tenants
  if (!activeUser || !activeTenant) {
    return <CenteredAccountError />;
  }

  const showWarningBanner = false;
  const warningBannerText = `Epicor Cloud is currently experiencing issues and we are unable to sync data for these customers.  If you are an Epicor Cloud customer, you can still view data in Recurrency, but data may be stale and you will not be able to export data back to P21.  For more details, see Epicor's status page at https://status.epicor.com/?componentId=80992. Our team is actively monitoring the situation.`;
  const showErrorBanner = false;
  const errorBannerText = `Our team is aware of an issue impacting syncs after exports. We are working to resolve this as soon as possible. We apologize for the inconvenience.`;
  const showSolutionsBanner = activeUser.email === 'solutions@recurrency.com';
  const solutionsBannerText =
    'This solutions account has full write permissions, be very careful when performing updates and sign out when you are not making them.';

  return (
    <ChatContextProvider>
      <div
        className={cx(
          'AppContainer',
          css`
            min-height: 100vh;
            position: relative;
            display: flex;
            background: white;
          `,
        )}
      >
        <Navigation isDesktopView={isDesktopView}>
          <div
            className={css`
              position: relative;
              background-color: white;
              margin-top: ${isDesktopView ? '0px' : '80px'};
              margin-bottom: 20px;
            `}
          >
            {isConnectionDown && <Alert noRadius message={connectionBannerText} banner closable type="error" />}
            {showErrorBanner && <Alert noRadius message={errorBannerText} banner type="error" />}
            {showSolutionsBanner && <Alert noRadius message={solutionsBannerText} banner type="error" />}
            {showWarningBanner && <Alert noRadius closable message={warningBannerText} banner type="warning" />}
            <IntegrationPausedBanner />
            <NoConnectionBanner />
            <UpgradeBanner />
            <IntercomUpdater />
            {children}
          </div>
        </Navigation>
        {hasChatWithDataAccess(activeTenant, activeUser) && <ChatAssistant />}
      </div>
    </ChatContextProvider>
  );
}
